<template>
	<TnCard
		class="card-horizontal"
		:class="{ compact }"
		v-bind="filteredAttrs"
		:hover="hoverSize"
		:shadow="!$attrs.border && !$attrs.dark ? 's' : ''"
		borderRadius="s"
		@click="$emit('click')"
	>
		<template #badge>
			<slot name="badge" />
		</template>
		<div
			v-if="$slots['graphic']"
			class="graphic"
		>
			<!-- @slot For graphic such as a small image, or UI-icon, left-hand side. -->
			<slot name="graphic" />
		</div>
		<div class="content__wrapper">
			<div class="content">
				<div class="content--left">
					<slot name="title">
						<TnParagraph
							v-if="title"
							bold
							size="l"
							responsive
							>{{ title }}</TnParagraph
						>
					</slot>
					<slot name="text">
						<TnParagraph
							v-if="text"
							class="sub-text"
							size="m"
							responsive
							>{{ text }}</TnParagraph
						>
					</slot>
				</div>
				<div class="content--right">
					<slot name="secondary-title">
						<TnParagraph
							v-if="secondaryTitle"
							bold
							size="l"
							responsive
							>{{ secondaryTitle }}</TnParagraph
						>
					</slot>
					<slot name="sub-text">
						<TnParagraph
							v-if="secondaryText"
							class="sub-text"
							size="m"
							responsive
							>{{ secondaryText }}</TnParagraph
						>
					</slot>
				</div>
			</div>
			<!-- @slot For any additonal content, placed below title and secondary title. -->
			<slot></slot>
		</div>
		<div
			class="actions"
			v-if="$slots['actions'] && !filteredAttrs.loading && !(filteredAttrs.to || filteredAttrs.href)"
		>
			<!-- @slot For action (such as a single button). Placement right hand side.
            NB! Will be ignored if to/href is provided and card is clickable -->
			<slot name="actions" />
		</div>
		<TnIcon
			v-if="arrow && !$slots['actions']"
			size="l"
			class="arrow"
			name="cta-right"
		/>
	</TnCard>
</template>

<script>
export default defineComponent({
	name: "TnCardHorizontal",

	props: {
		/* title */
		title: { type: String, default: "" },
		/* secondaryTitle */
		secondaryTitle: { type: String, default: "" },
		/* text */
		text: { type: String, default: "" },
		/* secondaryText */
		secondaryText: { type: String, default: "" },
		/* compact */
		compact: { type: Boolean, default: false },
		/* arrow */
		arrow: { type: Boolean, default: false },
	},
	emits: [/** * Event emitted when the card is clicked */ "click"],

	computed: {
		iconClasses() {
			return this.icon ? [`icon-${this.iconSize}`] : [];
		},
		hoverSize() {
			if (this.$slots["actions"]) {
				return false;
			}
			return this.$attrs.border ? "s" : "m";
		},
		/* Filter attributes to prevent passing through to TnCard/NuxtLink */
		filteredAttrs() {
			const attrs = this.$attrs;
			return attrs;
		},
	},
});
</script>

<style lang="scss" scoped>
@use "@/assets/scss/variables" as variables;
@use "@/assets/global-style/scss/mixins/all" as mixins;

.card-horizontal {
	--card-content-color: #{variables.$color-neutrals-black};
	--card-subtext-color: #{variables.$color-neutrals-600-shade};
	--card-graphic-color: #{variables.$color-primary-mid};
	--card-actions-color: #{variables.$color-cta-default};
	--card-actions-hover-color: #{variables.$color-cta-hover};

	width: 100%;
	min-height: 82px;
	display: flex;
	justify-content: space-between;
	flex-wrap: nowrap;
	align-items: center;
	padding: variables.$spacing-l;
	gap: variables.$spacing-m;
	position: relative;

	&.dark {
		--card-content-color: #{variables.$color-neutrals-25-tint};
		--card-subtext-color: #{variables.$color-neutrals-100-tint};
		--card-graphic-color: #{variables.$color-primary-light};
		--card-actions-color: #{variables.$color-cta-dark-default};
		--card-actions-hover-color: #{variables.$color-cta-dark-hover};
	}

	&.compact {
		padding: variables.$spacing-m;
	}

	&:hover {
		.actions {
			color: var(--card-actions-hover-color);
		}
	}

	.graphic {
		display: flex;
		justify-content: center;
		align-items: center;
		color: var(--card-graphic-color);
		width: 48px;
		height: 48px;
		flex-shrink: 0;
		flex-grow: 0;

		:deep() {
			> * {
				max-height: 100%;
				max-width: 100%;
			}
		}
	}

	.content {
		display: inline-flex;
		flex-wrap: nowrap;
		width: 100%;
		justify-content: space-between;

		* {
			color: var(--card-content-color);
		}

		&--left {
			text-align: left;
		}

		&--right {
			text-align: right;
			flex-direction: column;
			justify-content: center;
			display: flex;
			flex-shrink: 0;
			margin-left: variables.$spacing-m;
		}

		&__wrapper {
			width: 100%;
		}

		.sub-text {
			color: var(--card-subtext-color);
		}
	}

	.actions {
		color: var(--card-actions-color);
	}

	.arrow {
		color: var(--card-actions-color);
	}

	@include mixins.breakpoint(mobile) {
		gap: variables.$spacing-s;
	}
}
</style>
